import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, message } from 'antd';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/* ── palette ── */
const GOLD  = '#B8892A';
const TEAL  = '#1A6B54';
const INK   = '#18140C';
const MUTED = '#685840';
const BG    = '#FAF8F5';
const BD    = '#E8E0D0';
const RED   = '#C0392B';
const WHITE = '#FFFFFF';

const STATUS_KEYS = {
  live:    { key: 'live.streaming', color: WHITE, bg: RED },
  preview: { key: 'live.upcoming', color: INK, bg: '#F5EEDD' },
  idle:    { key: 'live.upcoming', color: INK, bg: '#F5EEDD' },
  ended:   { key: 'live.ended', color: MUTED, bg: '#EDE8E0' },
};

/* ── helpers ── */
function formatPrice(v) {
  if (v == null) return '--';
  return '\u00a5' + Number(v).toLocaleString('zh-CN', { minimumFractionDigits: 0 });
}

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/* ── gift definitions ── */
const GIFT_LIST_DEF = [
  { id: 'rose',     emoji: '\uD83C\uDF39', nameKey: 'live.giftRose',     price: 1 },
  { id: 'heart',    emoji: '\u2764\uFE0F',  nameKey: 'live.giftHeart',    price: 1 },
  { id: 'beer',     emoji: '\uD83C\uDF7A', nameKey: 'live.giftBeer',     price: 5 },
  { id: 'cake',     emoji: '\uD83C\uDF82', nameKey: 'live.giftCake',     price: 10 },
  { id: 'firework', emoji: '\uD83C\uDF86', nameKey: 'live.giftFirework', price: 52 },
  { id: 'diamond',  emoji: '\uD83D\uDC8E', nameKey: 'live.giftDiamond',  price: 100 },
  { id: 'crown',    emoji: '\uD83D\uDC51', nameKey: 'live.giftCrown',    price: 520 },
  { id: 'rocket',   emoji: '\uD83D\uDE80', nameKey: 'live.giftRocket',   price: 1000 },
];

/* ── common emoji set for picker ── */
const EMOJI_LIST = [
  '😀','😂','🤣','😍','🥰','😘','😎','🤩','🥺','😭',
  '👍','👏','🙏','💪','🎉','🔥','❤️','💯','✨','🌟',
  '💎','⌚','👜','💍','🎁','🏆','💰','🛍️','👀','🤝',
];

/* ── animation keyframes (injected once) ── */
let heartStyleInjected = false;
function injectHeartStyle() {
  if (heartStyleInjected) return;
  heartStyleInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes livewatch-heart-float {
      0%   { opacity: 1; transform: translateY(0) scale(1); }
      50%  { opacity: 0.8; transform: translateY(-40px) scale(1.2); }
      100% { opacity: 0; transform: translateY(-80px) scale(0.6); }
    }
    @keyframes livewatch-heart-pulse {
      0%   { transform: scale(1); }
      30%  { transform: scale(1.3); }
      100% { transform: scale(1); }
    }
    @keyframes livewatch-danmu-scroll {
      0%   { transform: translateX(100%); }
      100% { transform: translateX(-100%); }
    }
    @keyframes livewatch-gift-enter {
      0%   { opacity: 0; transform: translateX(-60px) scale(0.6); }
      20%  { opacity: 1; transform: translateX(0) scale(1.1); }
      40%  { transform: translateX(0) scale(1); }
      80%  { opacity: 1; transform: translateX(0) scale(1); }
      100% { opacity: 0; transform: translateX(40px) scale(0.8); }
    }
    @keyframes livewatch-gift-emoji-pop {
      0%   { transform: scale(0.3); opacity: 0; }
      50%  { transform: scale(1.3); opacity: 1; }
      100% { transform: scale(1); opacity: 1; }
    }
  `;
  document.head.appendChild(style);
}

export default function LiveWatch() {
  const { t } = useTranslation();
  const { id: roomId } = useParams();
  const navigate = useNavigate();
  const { user, isLoggedIn } = useAuth();

  const GIFT_LIST = GIFT_LIST_DEF.map(g => ({ ...g, name: t(g.nameKey) }));

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [loading, setLoading] = useState(true);
  const [room, setRoom] = useState(null);
  const [products, setProducts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [viewerCount, setViewerCount] = useState(0);
  const [likeCount, setLikeCount] = useState(0);
  const [heartAnims, setHeartAnims] = useState([]);
  const [productPanelOpen, setProductPanelOpen] = useState(false);
  const [highlightProductId, setHighlightProductId] = useState(null);
  const [error, setError] = useState(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [danmuList, setDanmuList] = useState([]);
  const [danmuEnabled, setDanmuEnabled] = useState(true);
  const [cartOpen, setCartOpen] = useState(false);
  const [giftPanelOpen, setGiftPanelOpen] = useState(false);
  const [giftAnims, setGiftAnims] = useState([]);

  const videoRef = useRef(null);
  const chatEndRef = useRef(null);
  const wsRef = useRef(null);
  const heartIdRef = useRef(0);
  const danmuIdRef = useRef(0);
  const giftIdRef = useRef(0);

  /* ── responsive ── */
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  /* ── inject heart animation style ── */
  useEffect(() => { injectHeartStyle(); }, []);

  /* ── fetch room data ── */
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api.get(`/live/${roomId}`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data || res.data;
        setRoom(data.room || data);
        setProducts(data.products || []);
        setViewerCount(data.room?.viewer_count || data.viewer_count || 0);
        setLikeCount(data.room?.like_count || data.like_count || 0);
        if (data.room?.highlight_product_id || data.highlight_product_id) {
          setHighlightProductId(data.room?.highlight_product_id || data.highlight_product_id);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.message || t('live.loadFailed', '加载直播间失败'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [roomId]);

  /* ── fetch chat history ── */
  useEffect(() => {
    api.get(`/live/${roomId}/messages`)
      .then((res) => {
        const msgs = res.data?.data || res.data || [];
        setMessages(Array.isArray(msgs) ? msgs : []);
      })
      .catch(() => { /* silent — chat history is optional */ });
  }, [roomId]);

  /* ── video playback ── */
  useEffect(() => {
    if (!room || !videoRef.current) return;
    const video = videoRef.current;
    let src = null;

    if (room.status === 'live' && room.pull_url_hls) {
      src = room.pull_url_hls;
    } else if (room.status === 'ended' && room.replay_url) {
      src = room.replay_url;
    }

    if (!src) { video.src = ''; return; }

    // Try native HLS (Safari) or fall back to direct source
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      video.play().catch(() => {});
    } else {
      // Attempt dynamic import of hls.js for non-Safari browsers
      import('hls.js').then(({ default: Hls }) => {
        if (!Hls.isSupported()) {
          video.src = src;
          video.play().catch(() => {});
          return;
        }
        const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
        hls.loadSource(src);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => { video.play().catch(() => {}); });
        // Cleanup when room or component changes
        video._hlsInstance = hls;
      }).catch(() => {
        // hls.js not installed — try direct playback
        video.src = src;
        video.play().catch(() => {});
      });
    }

    return () => {
      if (video._hlsInstance) {
        video._hlsInstance.destroy();
        delete video._hlsInstance;
      }
    };
  }, [room?.status, room?.pull_url_hls, room?.replay_url]);

  /* ── WebSocket ── */
  useEffect(() => {
    if (!roomId) return;
    const token = localStorage.getItem('token') || '';
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const wsUrl = `${protocol}://${window.location.host}/ws/live?room=${roomId}&token=${token}`;
    let ws;

    try {
      ws = new WebSocket(wsUrl);
    } catch {
      return;
    }

    ws.onopen = () => { wsRef.current = ws; };

    ws.onmessage = (evt) => {
      let msg;
      try { msg = JSON.parse(evt.data); } catch { return; }

      switch (msg.type) {
        case 'chat':
          setMessages((prev) => [...prev.slice(-200), msg]);
          // push to danmu overlay
          {
            const did = ++danmuIdRef.current;
            const lane = did % 5;
            setDanmuList((prev) => [...prev.slice(-30), { id: did, text: msg.content, nickname: msg.nickname || msg.user_name || msg.username || t('live.anonymous', '匿名'), lane }]);
            setTimeout(() => { setDanmuList((prev) => prev.filter((d) => d.id !== did)); }, 8000);
          }
          break;
        case 'enter':
          setMessages((prev) => [...prev.slice(-200), { ...msg, type: 'system', content: t('live.userEntered', { name: msg.nickname || msg.user_name || t('live.user', '用户'), defaultValue: '{{name}} 进入了直播间' }) }]);
          break;
        case 'system':
          setMessages((prev) => [...prev.slice(-200), msg]);
          break;
        case 'viewer_count':
          setViewerCount(msg.count ?? msg.viewer_count ?? 0);
          break;
        case 'like':
          setLikeCount((c) => c + (msg.count || 1));
          break;
        case 'gift': {
          const gDef = GIFT_LIST.find((g) => g.id === msg.gift_id) || { emoji: '🎁', name: t('live.giftLabel', '礼物') };
          const gid = ++giftIdRef.current;
          setGiftAnims((prev) => [...prev.slice(-5), {
            id: gid, emoji: gDef.emoji, name: gDef.name,
            nickname: msg.nickname || msg.username || t('live.anonymous', '匿名'),
          }]);
          setTimeout(() => { setGiftAnims((prev) => prev.filter((g) => g.id !== gid)); }, 4000);
          setMessages((prev) => [...prev.slice(-200), {
            type: 'system',
            content: t('live.giftSent', { name: msg.nickname || t('live.anonymous', '匿名'), emoji: gDef.emoji, gift: gDef.name, defaultValue: '{{name}} 送出了 {{emoji}} {{gift}}' }),
          }]);
          break;
        }
        case 'product_highlight':
          setHighlightProductId(msg.product_id || msg.consignment_id || null);
          break;
        default:
          break;
      }
    };

    ws.onclose = () => { wsRef.current = null; };
    ws.onerror = () => { ws.close(); };

    return () => {
      ws.close();
      wsRef.current = null;
    };
  }, [roomId]);

  /* ── auto-scroll chat ── */
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /* ── send chat ── */
  const sendChat = useCallback(() => {
    const text = chatInput.trim();
    if (!text) return;
    if (!isLoggedIn) { message.warning(t('live.loginFirst', '请先登录')); return; }
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      message.warning(t('live.connectionLost', '连接中断，请刷新页面'));
      return;
    }
    wsRef.current.send(JSON.stringify({ type: 'chat', content: text }));
    setChatInput('');
  }, [chatInput, isLoggedIn]);

  /* ── send like ── */
  const sendLike = useCallback(() => {
    if (!isLoggedIn) { message.warning(t('live.loginFirst', '请先登录')); return; }
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'like' }));
    }
    setLikeCount((c) => c + 1);
    // heart float animation
    const hid = ++heartIdRef.current;
    setHeartAnims((prev) => [...prev.slice(-10), hid]);
    setTimeout(() => {
      setHeartAnims((prev) => prev.filter((x) => x !== hid));
    }, 1200);
  }, [isLoggedIn]);

  /* ── key handler for chat ── */
  const handleChatKey = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); }
  }, [sendChat]);

  /* ── send gift ── */
  const sendGift = useCallback((gift) => {
    if (!isLoggedIn) { message.warning(t('live.loginFirst', '请先登录')); return; }
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      message.warning(t('live.connectionLost', '连接中断，请刷新页面'));
      return;
    }
    wsRef.current.send(JSON.stringify({ type: 'gift', gift_id: gift.id }));
    // local animation immediately
    const gid = ++giftIdRef.current;
    setGiftAnims((prev) => [...prev.slice(-5), {
      id: gid, emoji: gift.emoji, name: gift.name,
      nickname: user?.nickname || user?.name || t('live.me', '我'),
    }]);
    setTimeout(() => { setGiftAnims((prev) => prev.filter((g) => g.id !== gid)); }, 4000);
    setGiftPanelOpen(false);
  }, [isLoggedIn, user]);

  /* ──────────── RENDER ──────────── */

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: BG }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', background: BG, padding: 24 }}>
        <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>!</div>
        <div style={{ color: INK, fontSize: 16, marginBottom: 8 }}>{error}</div>
        <button onClick={() => navigate(-1)} style={{ ...btnBase, background: GOLD, color: WHITE, padding: '8px 32px', marginTop: 12 }}>{t('common.back')}</button>
      </div>
    );
  }

  if (!room) return null;

  const statusDef = STATUS_KEYS[room.status] || STATUS_KEYS.idle;
  const status = { label: t(statusDef.key), color: statusDef.color, bg: statusDef.bg };
  const isLive = room.status === 'live';
  const isEnded = room.status === 'ended';
  const showVideo = isLive || (isEnded && room.replay_url);

  /* ── sub-renders ── */

  const renderHeader = () => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
      background: WHITE, borderBottom: `1px solid ${BD}`, position: 'sticky', top: 0, zIndex: 20,
    }}>
      <button onClick={() => navigate(-1)} style={{ ...btnBase, fontSize: 22, color: INK, padding: 0, lineHeight: 1 }} aria-label={t('common.back')}>
        &#8592;
      </button>
      {room.host_avatar && (
        <img src={room.host_avatar} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', border: `1px solid ${BD}` }} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 15, color: INK, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {room.title || t('live.defaultRoom', '直播间')}
        </div>
        {room.host_name && (
          <div style={{ fontSize: 12, color: MUTED }}>{room.host_name}</div>
        )}
      </div>
      <span style={{
        fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 12,
        color: status.color, background: status.bg, whiteSpace: 'nowrap',
      }}>
        {status.label}
      </span>
    </div>
  );

  const renderVideoArea = () => (
    <div style={{ position: 'relative', background: '#000', width: '100%', aspectRatio: isMobile ? '16/9' : undefined, height: isMobile ? undefined : '100%', minHeight: isMobile ? undefined : 300 }}>
      {showVideo ? (
        <video
          ref={videoRef}
          playsInline
          controls
          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
      ) : (
        <div style={{
          width: '100%', height: '100%', minHeight: isMobile ? 200 : 400,
          display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
          backgroundImage: room.cover_url ? `url(${room.cover_url})` : undefined,
          backgroundSize: 'cover', backgroundPosition: 'center',
          position: 'relative',
        }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} />
          <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', color: WHITE }}>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
              {room.status === 'ended' ? t('live.liveEnded', '直播已结束') : t('live.comingSoon', '即将开播')}
            </div>
            {room.scheduled_at && room.status !== 'ended' && (
              <div style={{ fontSize: 14, opacity: 0.85 }}>
                {t('live.startTime', '开播时间')}: {formatTime(room.scheduled_at)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Danmu overlay ── */}
      {danmuEnabled && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 4 }}>
          {danmuList.map((d) => (
            <div key={d.id} style={{
              position: 'absolute',
              top: 10 + d.lane * 32,
              whiteSpace: 'nowrap',
              fontSize: 14, fontWeight: 500,
              color: '#fff', textShadow: '1px 1px 3px rgba(0,0,0,0.7)',
              animation: 'livewatch-danmu-scroll 8s linear forwards',
              willChange: 'transform',
            }}>
              <span style={{ color: GOLD, marginRight: 6, fontSize: 12 }}>{d.nickname}</span>
              {d.text}
            </div>
          ))}
        </div>
      )}

      {/* ── Danmu toggle ── */}
      <button
        onClick={() => setDanmuEnabled((v) => !v)}
        style={{
          ...btnBase, position: 'absolute', top: 10, right: 10, zIndex: 6,
          background: danmuEnabled ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.3)',
          color: WHITE, fontSize: 11, padding: '3px 10px', borderRadius: 12,
        }}
      >
        {danmuEnabled ? t('live.danmuOn', '弹幕 ON') : t('live.danmuOff', '弹幕 OFF')}
      </button>

      {/* ── Shopping Cart Float (小黄车) ── */}
      {products.length > 0 && (
        <button
          onClick={() => setCartOpen((v) => !v)}
          style={{
            ...btnBase, position: 'absolute', bottom: isMobile ? 10 : 16, left: 10, zIndex: 6,
            width: 44, height: 44, borderRadius: '50%',
            background: 'rgba(200,146,42,0.9)', color: WHITE,
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
            fontSize: 20,
          }}
          aria-label={t('live.productList', '商品列表')}
        >
          🛒
          <span style={{
            position: 'absolute', top: -4, right: -4,
            minWidth: 18, height: 18, borderRadius: 9,
            background: RED, color: WHITE, fontSize: 10, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 4px',
          }}>{products.length}</span>
        </button>
      )}

      {/* overlays */}
      <div style={{
        position: 'absolute', top: 10, left: products.length > 0 ? 64 : 10, display: 'flex', gap: 8, zIndex: 5,
      }}>
        {/* viewer count */}
        <span style={{
          background: 'rgba(0,0,0,0.55)', color: WHITE, fontSize: 12, padding: '3px 10px',
          borderRadius: 12, display: 'flex', alignItems: 'center', gap: 4,
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>
          {viewerCount}
        </span>
      </div>

      {/* like count + button */}
      <div style={{
        position: 'absolute', bottom: isMobile ? 10 : 16, right: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, zIndex: 5,
      }}>
        <div style={{ position: 'relative' }}>
          {heartAnims.map((hid) => (
            <span key={hid} style={{
              position: 'absolute', bottom: '100%', left: '50%', marginLeft: -10,
              fontSize: 20, color: RED, pointerEvents: 'none',
              animation: 'livewatch-heart-float 1.2s ease-out forwards',
            }}>&#10084;</span>
          ))}
          <button
            onClick={sendLike}
            style={{
              ...btnBase, width: 44, height: 44, borderRadius: '50%', background: 'rgba(0,0,0,0.5)',
              color: RED, fontSize: 22, display: 'flex', justifyContent: 'center', alignItems: 'center',
              animation: heartAnims.length ? 'livewatch-heart-pulse 0.3s ease' : undefined,
            }}
            aria-label={t('live.likes')}
          >&#10084;</button>
        </div>
        <span style={{ color: WHITE, fontSize: 11, textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>
          {likeCount}
        </span>
      </div>
    </div>
  );

  const renderChat = () => (
    <div style={{
      display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0,
      background: WHITE, borderTop: isMobile ? `1px solid ${BD}` : 'none',
      borderLeft: isMobile ? 'none' : `1px solid ${BD}`,
    }}>
      {/* chat messages */}
      <div style={{
        flex: 1, overflowY: 'auto', padding: '10px 14px',
        minHeight: isMobile ? 180 : 300,
        maxHeight: isMobile ? 260 : undefined,
      }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', color: MUTED, fontSize: 13, paddingTop: 40 }}>{t('notifications.empty')}</div>
        )}
        {messages.map((msg, i) => {
          if (msg.type === 'system') {
            return (
              <div key={i} style={{
                textAlign: 'center', fontSize: 12, color: MUTED, margin: '6px 0',
                padding: '3px 12px', background: '#F6F3EE', borderRadius: 10, display: 'inline-block',
                width: '100%',
              }}>
                {msg.content}
              </div>
            );
          }
          return (
            <div key={i} style={{ margin: '5px 0', fontSize: 14, lineHeight: 1.5 }}>
              <span style={{ color: GOLD, fontWeight: 600, marginRight: 6, fontSize: 13 }}>
                {msg.nickname || msg.user_name || msg.username || t('live.anonymous', '匿名')}
              </span>
              <span style={{ color: INK }}>{msg.content}</span>
            </div>
          );
        })}
        <div ref={chatEndRef} />
      </div>

      {/* emoji picker */}
      {emojiOpen && (
        <div style={{
          padding: '8px 12px', borderTop: `1px solid ${BD}`, background: '#FAFAF8',
          display: 'flex', flexWrap: 'wrap', gap: 2,
        }}>
          {EMOJI_LIST.map((em) => (
            <button
              key={em}
              onClick={() => { setChatInput((v) => v + em); setEmojiOpen(false); }}
              style={{ ...btnBase, fontSize: 22, padding: '4px 6px', borderRadius: 6, cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#EDE8E0'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >{em}</button>
          ))}
        </div>
      )}

      {/* chat input */}
      <div style={{
        display: 'flex', gap: 8, padding: '10px 14px', alignItems: 'center',
        borderTop: `1px solid ${BD}`, background: BG,
      }}>
        <button
          onClick={() => setEmojiOpen((v) => !v)}
          style={{
            ...btnBase, fontSize: 20, padding: '4px 6px', borderRadius: 6, flexShrink: 0,
            color: emojiOpen ? GOLD : MUTED,
          }}
          aria-label={t('live.emojiLabel', '表情')}
        >😊</button>
        <button
          onClick={() => { setGiftPanelOpen((v) => !v); setEmojiOpen(false); }}
          style={{
            ...btnBase, fontSize: 20, padding: '4px 6px', borderRadius: 6, flexShrink: 0,
            color: giftPanelOpen ? GOLD : MUTED,
          }}
          aria-label={t('live.giftLabel', '礼物')}
        >🎁</button>
        <input
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          onKeyDown={handleChatKey}
          onFocus={() => setEmojiOpen(false)}
          placeholder={isLoggedIn ? t('live.commentPlaceholder') : t('live.loginToChat', '登录后参与聊天')}
          disabled={!isLoggedIn}
          maxLength={200}
          style={{
            flex: 1, border: `1px solid ${BD}`, borderRadius: 20, padding: '8px 16px',
            fontSize: 14, outline: 'none', background: WHITE, color: INK,
          }}
        />
        <button
          onClick={sendChat}
          disabled={!isLoggedIn || !chatInput.trim()}
          style={{
            ...btnBase, background: GOLD, color: WHITE, padding: '8px 18px', borderRadius: 20,
            fontSize: 14, fontWeight: 600, opacity: (!isLoggedIn || !chatInput.trim()) ? 0.5 : 1,
          }}
        >
          {t('chat.send')}
        </button>
      </div>
    </div>
  );

  const renderProductItem = (p) => {
    const pid = p.consignment_id || p.id;
    const isHighlight = String(pid) === String(highlightProductId);
    return (
      <div
        key={pid}
        style={{
          display: 'flex', gap: 12, padding: '12px 16px',
          borderBottom: `1px solid ${BD}`,
          background: isHighlight ? '#FFF9EE' : WHITE,
          cursor: 'pointer', transition: 'background 0.15s',
        }}
        onClick={() => navigate(`/market/${pid}`)}
      >
        <div style={{
          width: 72, height: 72, borderRadius: 8, overflow: 'hidden', flexShrink: 0,
          background: BG, border: `1px solid ${BD}`,
        }}>
          {p.thumbnail || p.cover_url ? (
            <img src={p.thumbnail || p.cover_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', color: MUTED, fontSize: 12 }}>{t('live.noImage', '无图')}</div>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              fontSize: 14, fontWeight: 600, color: INK,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1,
            }}>
              {p.name || p.title || t('live.productLabel', '商品')}
            </span>
            {isHighlight && (
              <span style={{
                fontSize: 11, fontWeight: 600, color: RED, background: '#FDECEA',
                padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap', flexShrink: 0,
              }}>{t('liveWatch.explaining')}</span>
            )}
          </div>
          {p.brand && <div style={{ fontSize: 12, color: MUTED }}>{p.brand}</div>}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            {p.is_flash_sale && p.flash_price != null ? (
              <>
                <span style={{ fontSize: 16, fontWeight: 700, color: RED }}>{formatPrice(p.flash_price)}</span>
                <span style={{ fontSize: 12, color: MUTED, textDecoration: 'line-through' }}>{formatPrice(p.price)}</span>
              </>
            ) : (
              <span style={{ fontSize: 16, fontWeight: 700, color: GOLD }}>{formatPrice(p.price)}</span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (!isLoggedIn) { message.warning(t('live.loginFirst', '请先登录')); return; }
              navigate(`/market/${pid}`);
            }}
            style={{
              ...btnBase, background: TEAL, color: WHITE, padding: '6px 16px',
              borderRadius: 18, fontSize: 13, fontWeight: 600,
            }}
          >
            {t('live.buyNow')}
          </button>
        </div>
      </div>
    );
  };

  const renderProducts = () => (
    <div style={{
      background: WHITE,
      borderTop: isMobile ? `1px solid ${BD}` : 'none',
    }}>
      <button
        onClick={() => setProductPanelOpen((v) => !v)}
        style={{
          ...btnBase, width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '12px 16px', borderBottom: productPanelOpen ? `1px solid ${BD}` : 'none',
          fontSize: 15, fontWeight: 600, color: INK,
        }}
      >
        <span>{t('live.productList', '商品列表')} ({products.length})</span>
        <span style={{ fontSize: 12, color: MUTED, transform: productPanelOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
          &#9660;
        </span>
      </button>
      {productPanelOpen && (
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {products.length === 0 && (
            <div style={{ textAlign: 'center', color: MUTED, padding: 24, fontSize: 13 }}>{t('liveWatch.noProducts')}</div>
          )}
          {products.map(renderProductItem)}
        </div>
      )}
    </div>
  );

  /* ── Gift animation overlay ── */
  const renderGiftAnims = () => {
    if (giftAnims.length === 0) return null;
    return (
      <div style={{ position: 'fixed', left: 12, bottom: isMobile ? 140 : 120, zIndex: 700, pointerEvents: 'none' }}>
        {giftAnims.map((g, i) => (
          <div key={g.id} style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
            background: 'rgba(0,0,0,0.55)', borderRadius: 24, padding: '6px 16px 6px 10px',
            animation: 'livewatch-gift-enter 4s ease-out forwards',
          }}>
            <span style={{ fontSize: 28, animation: 'livewatch-gift-emoji-pop 0.5s ease-out forwards' }}>{g.emoji}</span>
            <div>
              <div style={{ color: GOLD, fontSize: 12, fontWeight: 600 }}>{g.nickname}</div>
              <div style={{ color: WHITE, fontSize: 13 }}>{t('liveWatch.sentGift')} {g.name}</div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  /* ── Gift panel ── */
  const renderGiftPanel = () => {
    if (!giftPanelOpen) return null;
    return (
      <>
        <div onClick={() => setGiftPanelOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 810 }} />
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 811,
          background: WHITE, borderRadius: '16px 16px 0 0',
          boxShadow: '0 -8px 30px rgba(0,0,0,0.2)',
          paddingBottom: 'env(safe-area-inset-bottom, 16px)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 4 }}>
            <div style={{ width: 40, height: 4, borderRadius: 2, background: '#C8C0B4' }} />
          </div>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '6px 16px 10px', borderBottom: `1px solid ${BD}`,
          }}>
            <span style={{ fontWeight: 600, fontSize: 16, color: INK }}>{t('liveWatch.sendGift')}</span>
            <button onClick={() => setGiftPanelOpen(false)} style={{ ...btnBase, fontSize: 20, color: MUTED, padding: '2px 6px' }}>×</button>
          </div>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8,
            padding: '16px 12px',
          }}>
            {GIFT_LIST.map((g) => (
              <button
                key={g.id}
                onClick={() => sendGift(g)}
                style={{
                  ...btnBase, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                  padding: '12px 4px', borderRadius: 12, border: `1px solid ${BD}`,
                  background: WHITE, cursor: 'pointer', transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#FFF9EE'; e.currentTarget.style.borderColor = GOLD; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = WHITE; e.currentTarget.style.borderColor = BD; }}
              >
                <span style={{ fontSize: 32 }}>{g.emoji}</span>
                <span style={{ fontSize: 12, color: INK, fontWeight: 500 }}>{g.name}</span>
                <span style={{ fontSize: 11, color: GOLD }}>{g.price} {t('liveWatch.coins')}</span>
              </button>
            ))}
          </div>
        </div>
      </>
    );
  };

  /* ── Cart popup (小黄车) ── */
  const renderCartPopup = () => {
    if (!cartOpen || products.length === 0) return null;
    return (
      <>
        <div onClick={() => setCartOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 800 }} />
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 801,
          background: WHITE, borderRadius: '16px 16px 0 0',
          maxHeight: '60vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 -8px 30px rgba(0,0,0,0.2)',
        }}>
          <div style={{
            display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 4,
          }}>
            <div style={{ width: 40, height: 4, borderRadius: 2, background: '#C8C0B4' }} />
          </div>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '8px 16px 12px', borderBottom: `1px solid ${BD}`,
          }}>
            <span style={{ fontWeight: 600, fontSize: 16, color: INK }}>
              {t('liveWatch.roomProducts')} ({products.length})
            </span>
            <button onClick={() => setCartOpen(false)} style={{ ...btnBase, fontSize: 20, color: MUTED, padding: '2px 6px' }}>×</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 'env(safe-area-inset-bottom, 16px)' }}>
            {products.map(renderProductItem)}
          </div>
        </div>
      </>
    );
  };

  /* ── layout ── */

  if (isMobile) {
    return (
      <div style={{ background: BG, minHeight: '100vh', paddingBottom: 60 }}>
        {renderHeader()}
        {renderVideoArea()}
        {renderProducts()}
        {renderChat()}
        {renderCartPopup()}
        {renderGiftPanel()}
        {renderGiftAnims()}
      </div>
    );
  }

  // desktop: left video + right chat, products below video
  return (
    <div style={{ background: BG, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {renderHeader()}
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* left column */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <div style={{ flex: 1, minHeight: 400 }}>
            {renderVideoArea()}
          </div>
          {renderProducts()}
        </div>
        {/* right column: chat */}
        <div style={{ width: 360, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {renderChat()}
        </div>
      </div>
      {renderCartPopup()}
      {renderGiftPanel()}
      {renderGiftAnims()}
    </div>
  );
}

/* ── shared button reset ── */
const btnBase = {
  border: 'none',
  cursor: 'pointer',
  background: 'transparent',
  fontFamily: 'inherit',
};
